import * as React from 'react';
import { styled } from '../../../../../theme';
import { FormikContext, useFormik } from 'formik';
import { useMFAStore } from '../../../../../hooks/useMFAStore';
import { CenterFormOrientation, CenterFormWidget } from '../../../../../widgets/layout/CenterFormWidget';
import { FormLayoutWidget } from '../../../../../widgets/layout/FormLayoutWidget';
import { GroupedButtons } from '../../../../../widgets/buttons/GroupedButtons';
import { ButtonType } from '../../../../../widgets/buttons/ButtonWidget';
import { useSystem } from '../../../../../hooks/useSystem';
import { wrapLoadingAction } from '../../../../../utils/actions';
import { FormikOTPAuthenticationWidget } from '../../../../../widgets/authentication/FormikOTPAuthenticationWidget';
import { SmartGenerateBackupCodesWidget } from '../../../../../widgets-smart/SmartGenerateBackupCodesWidget';
import { FormikOTPWidget } from '../../../../../widgets/inputs';
import { ConfigureMFANavigationAction, SetupMFASteps } from '../ConfigureMFADialog';
import { MFABreadCrumb, MFAFormContent } from '../SharedMFADialogComponents';

export type OTPTestWidgetProps = {
  factorId: string;
  otpLength: number;
  pendingValidationContent: () => JSX.Element;
  back: ConfigureMFANavigationAction;
  next: ConfigureMFANavigationAction;
  onCancel: ConfigureMFANavigationAction;
};

type OTPTestFormikValues = {
  validated: boolean;
  testOTP: string;
};

namespace S {
  const BASE_LABEL = 'otp-mfa-test-widget';

  export const Container = styled.div``;

  export const OTPEntry = styled(FormikOTPWidget)`
    display: flex;
    justify-content: center;

    margin-top: 15px;
    margin-bottom: 15px;
  `;

  export const Message = styled.div`
    label: ${BASE_LABEL}-message;
    font-size: 14px;
    color: ${(p) => p.theme.text.secondary};
    text-align: center;
  `;
}

export const OTPMFATestWidget: React.FC<OTPTestWidgetProps> = (props) => {
  const { otpLength } = props;

  const system = useSystem();
  const mfaStore = useMFAStore();

  const factor = mfaStore.item(props.factorId);
  const [loading, setLoading] = React.useState(false);

  const formik = useFormik<OTPTestFormikValues>({
    initialValues: {
      validated: false,
      testOTP: ''
    },
    validateOnBlur: true,
    validateOnChange: false,
    validate: (values) => {
      if (!values.testOTP) {
        return {
          testOTP: 'This is required'
        };
      }

      if (!values.validated) {
        return { testOTP: 'Could not validate code' };
      }
      return {};
    },
    onSubmit: async (values) => {
      try {
        await factor.setAsPreferred();
        return true;
      } catch (error) {
        console.error(error);
        return system.dialogStore.errorDialog({
          errorMessage: `Could not configure ${factor.name} multi-factor authentication. See console for details.`
        });
      }
    }
  });

  const { testOTP, validated } = formik.values;

  React.useEffect(() => {
    if (testOTP.length < otpLength) {
      return;
    }

    wrapLoadingAction(
      async () => {
        const result = await factor.verify(testOTP);
        if (!result.success) {
          formik.setFieldError('testOTP', 'Invalid code entered');
          return;
        }
        formik.setFieldValue('validated', true);
        formik.setFieldError('testOTP', undefined);
      },
      setLoading,
      (error) => {
        console.error(error);
        formik.setFieldError('testOTP', 'Could not validate code');
      }
    );
  }, [testOTP]);

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget noLogo={true} primaryLabel={'Configure MFA'} orientation={CenterFormOrientation.ROW}>
        <MFABreadCrumb steps={SetupMFASteps} currentStep={2} />
        <FormLayoutWidget
          sections={[
            {
              key: 'content',
              elements: [
                <MFAFormContent key="content">
                  <FormikOTPAuthenticationWidget
                    name={'testOTP'}
                    disabled={loading || validated}
                    messageContent={() => (
                      <S.Container>
                        {validated ? (
                          <S.Message>The code above has successfully been validated</S.Message>
                        ) : (
                          props.pendingValidationContent?.()
                        )}
                      </S.Container>
                    )}
                  />
                  {validated ? <SmartGenerateBackupCodesWidget showMessage={true} /> : null}
                </MFAFormContent>
              ]
            },
            {
              key: 'buttons',
              elements: [
                <GroupedButtons
                  key="buttons"
                  btns={[
                    {
                      label: 'Cancel',
                      type: ButtonType.TERTIARY,
                      action: props.onCancel
                    },
                    {
                      label: 'Back',
                      type: ButtonType.SECONDARY,
                      action: props.back
                    },
                    {
                      label: 'Next',
                      action: async (event, loading) => {
                        loading(true);
                        const response = await formik.submitForm();
                        if (response) {
                          props.next(event, loading);
                        } else {
                          loading(false);
                        }
                      }
                    }
                  ]}
                />
              ]
            }
          ]}
        />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};
